import React from "react";
import {
  Box,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { MetricsTableColumn } from "./MetricsComparisonTable";
import type { DemographicsTableGroup, DemographicsCellFormat } from "./metricsTables";
import { fmtMetric } from "./buildPlatformMetrics";
import {
  getMetricsTableMinWidth,
  metricsTableBaseSx,
  metricsTableDataHeaderCellSx,
  metricsTableLabelRowCellSx,
  metricsTableStickyHeaderLabelColSx,
} from "./metricsTableLayout";
import { MetricsTableColgroup } from "./MetricsTableColgroup";

const COLUMN_COLORS: Record<string, string> = {
  youtube: "#FF0000",
  instagram: "#E1306C",
  tiktok: "#25B4C4",
  facebook: "#1877F2",
};

const DEFAULT_BAR_COLOR = "#667eea";

function DemographicsBarCell({
  value,
  color,
  format = "percent",
}: {
  value: number | null;
  color: string;
  format?: DemographicsCellFormat;
}) {
  if (value == null) {
    return (
      <Typography variant="body2" color="text.disabled">
        —
      </Typography>
    );
  }
  if (format === "count") {
    return (
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{ fontVariantNumeric: "tabular-nums" }}
      >
        {fmtMetric(value)}
      </Typography>
    );
  }
  return (
    <Box>
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{ fontVariantNumeric: "tabular-nums", mb: 0.5 }}
      >
        {value.toFixed(1)}%
      </Typography>
      <LinearProgress
        variant="determinate"
        value={Math.min(100, value)}
        sx={{
          height: 8,
          borderRadius: 3,
          backgroundColor: "rgba(0,0,0,0.08)",
          "& .MuiLinearProgress-bar": {
            backgroundColor: color,
            borderRadius: 3,
          },
        }}
      />
    </Box>
  );
}

/**
 * Demographics comparison: platforms as columns, each cell a proportional
 * horizontal bar (same visual language as the YouTube geography block).
 */
export const DemographicsBarsTable: React.FC<{
  columns: MetricsTableColumn[];
  groups: DemographicsTableGroup[];
}> = ({ columns, groups }) => {
  const colCount = columns.length + 1;

  return (
    <Box sx={{ width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
      <Table
        size="small"
        sx={{
          ...metricsTableBaseSx,
          minWidth: getMetricsTableMinWidth(columns.length),
          "& th, & td": { verticalAlign: "top" },
        }}
      >
        <MetricsTableColgroup dataColumnCount={columns.length} />
        <TableHead>
          <TableRow>
            <TableCell sx={metricsTableStickyHeaderLabelColSx} />
            {columns.map((col) => (
              <TableCell key={col.id} sx={metricsTableDataHeaderCellSx}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  {col.icon ? (
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {col.icon}
                    </Box>
                  ) : null}
                  <Typography variant="body2" fontWeight={700} whiteSpace="nowrap">
                    {col.label}
                  </Typography>
                </Stack>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((group) => (
            <React.Fragment key={group.key}>
              <TableRow>
                <TableCell
                  colSpan={colCount}
                  sx={{ bgcolor: "action.hover", py: 0.5 }}
                >
                  <Typography
                    variant="overline"
                    fontWeight={700}
                    color="text.secondary"
                  >
                    {group.label}
                  </Typography>
                </TableCell>
              </TableRow>
              {group.rows.map((row) => (
                <TableRow key={row.key} hover>
                  <TableCell component="th" scope="row" sx={metricsTableLabelRowCellSx}>
                    <Typography variant="body2" component="span" fontWeight={500} color="text.secondary">
                      {row.label}
                    </Typography>
                  </TableCell>
                  {row.values.map((value, idx) => (
                    <TableCell key={columns[idx]!.id}>
                      <DemographicsBarCell
                        value={value}
                        format={row.format}
                        color={
                          row.color ??
                          COLUMN_COLORS[columns[idx]!.id] ??
                          DEFAULT_BAR_COLOR
                        }
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
